import type { Role } from '@keel/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import type { MeResponse, Preferences } from '@/api/contract';
import { isAuthError, useTRPC } from '@/api/query';
import { useAppStore, type AppState } from './store';

export type SessionStatus = 'loading' | 'authenticated' | 'anonymous';
const EDIT_ROLES: ReadonlySet<Role> = new Set(['owner', 'editor']);

/**
 * Current user, memberships and the selected entity (from `auth.me`). `entityId` falls back to the first
 * non-archived membership when the stored selection is gone.
 */
export const useSession = () => {
  const trpc = useTRPC();
  const q = useQuery(trpc.auth.me.queryOptions(undefined, { meta: { silent: true }, retry: false, staleTime: 60_000 }));
  const me: MeResponse | null | undefined = q.data;
  const currentEntityId = useAppStore((s) => s.currentEntityId);
  const consolidated = useAppStore((s) => s.consolidated);
  const memberships = (me?.memberships ?? []).filter((m) => !m.entity.isArchived);
  const membership = memberships.find((m) => m.entityId === currentEntityId) ?? memberships[0];
  const status: SessionStatus = q.isPending ? 'loading' : me?.user ? 'authenticated' : 'anonymous';
  return {
    status,
    error: q.error && !isAuthError(q.error) ? q.error : null,
    user: me?.user,
    memberships,
    membership,
    entity: membership?.entity,
    entityId: membership?.entityId,
    role: membership?.role,
    canEdit: membership ? EDIT_ROLES.has(membership.role) : false,
    consolidated: consolidated && memberships.length > 1,
    refetch: q.refetch,
  };
};

type PrefKey = 'theme' | 'calmMode' | 'reducedMotion' | 'privacyBlur';

/**
 * Read/write user preferences. Writes update the local store immediately (optimistic) and persist via
 * `auth.updatePreferences` when signed in.
 */
export const usePreferences = () => {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const set = useAppStore((s) => s.set);
  const theme = useAppStore((s) => s.theme);
  const calmMode = useAppStore((s) => s.calmMode);
  const reducedMotion = useAppStore((s) => s.reducedMotion);
  const privacyBlur = useAppStore((s) => s.privacyBlur);
  const mutation = useMutation(
    trpc.auth.updatePreferences.mutationOptions({
      meta: { silent: true },
      onSettled: () => qc.invalidateQueries({ queryKey: trpc.auth.me.queryKey() }),
    }),
  );
  const authenticated = useSession().status === 'authenticated';

  const setPreference = useCallback(
    <K extends PrefKey>(key: K, value: AppState[K]) => {
      set({ [key]: value } as Pick<AppState, K>);
      if (authenticated) mutation.mutate({ [key]: value } as Preferences);
    },
    [authenticated, mutation, set],
  );
  const savePreferences = useCallback((prefs: Preferences) => mutation.mutateAsync(prefs), [mutation]);

  return { theme, calmMode, reducedMotion, privacyBlur, setPreference, savePreferences, saving: mutation.isPending };
};
